import { LiveValue } from '@/model';
import type { CallMedia, MediaSample, MediaView } from './call-media';
import type { IceServer, SignalPayload } from './call-wire';

interface Options {
  devices?: MediaDevices;
  createPeer?: (config: RTCConfiguration) => RTCPeerConnection;
  createStream?: () => MediaStream;
  createAudio?: () => HTMLAudioElement;
}
type OutputDevices = MediaDevices & { selectAudioOutput?: () => Promise<MediaDeviceInfo> };

export function supportsCalls(): boolean {
  return window.isSecureContext === true && typeof RTCPeerConnection !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/** Native browser boundary. No media, identifiers, SDP or ICE leave this in-memory instance except signaling. */
export class BrowserMedia implements CallMedia {
  readonly view: LiveValue<MediaView>;
  onCandidate?: CallMedia['onCandidate'];
  onConnection?: CallMedia['onConnection'];
  onVideoFailure?: () => void;
  private readonly devices: MediaDevices | undefined;
  private readonly createPeer: (config: RTCConfiguration) => RTCPeerConnection;
  private readonly stream: () => MediaStream;
  private readonly audio: HTMLAudioElement;
  private peer: RTCPeerConnection | null = null;
  private audioTrack: MediaStreamTrack | null = null;
  private audioStream: MediaStream | null = null;
  private videoSender: RTCRtpSender | null = null;
  private audioSender: RTCRtpSender | null = null;
  private audioStart: Promise<void> | null = null;
  private closed = false;
  private muted = false;
  private videoEpoch = 0;

  constructor(options: Options = {}) {
    this.devices = options.devices ?? navigator.mediaDevices;
    this.createPeer = options.createPeer ?? (config => new RTCPeerConnection(config));
    this.stream = options.createStream ?? (() => new MediaStream());
    this.audio = options.createAudio?.() ?? document.createElement('audio');
    this.audio.autoplay = true; this.audio.setAttribute('playsinline', '');
    this.view = new LiveValue<MediaView>({ localVideo: null, remoteVideo: null, remoteAudio: null, cameras: [], selectedCamera: '', mirrored: true, outputs: [], selectedOutput: '', outputSupported: typeof this.audio.setSinkId === 'function', playbackBlocked: false });
    this.devices?.addEventListener('devicechange', this.devicesChanged);
  }
  private devicesChanged = () => { void this.refreshDevices().catch(() => undefined); };
  private patch(patch: Partial<MediaView>) { this.view.set({ ...this.view.get(), ...patch }); }
  private checkOpen() { if (this.closed) throw new DOMException('call_closed', 'AbortError'); }

  async acquireAudio(): Promise<void> {
    this.checkOpen();
    if (this.audioTrack?.readyState === 'live') return;
    if (this.audioStart) return this.audioStart;
    const devices = this.devices;
    if (!devices?.getUserMedia) throw new DOMException('microphone_unavailable', 'NotFoundError');
    // Invocation happens synchronously in the person's start/answer gesture, before the first await.
    this.audioStart = (async () => {
      const stream = await devices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      if (this.closed) { stream.getTracks().forEach(t => t.stop()); this.checkOpen(); }
      const track = stream.getAudioTracks()[0];
      if (!track) { stream.getTracks().forEach(t => t.stop()); throw new DOMException('microphone_unavailable', 'NotFoundError'); }
      this.audioStream?.getTracks().forEach(t => t.stop());
      this.audioStream = stream; this.audioTrack = track; track.enabled = !this.muted;
      if (this.audioSender) await this.audioSender.replaceTrack(track);
      if (this.closed) { stream.getTracks().forEach(t => t.stop()); this.checkOpen(); }
      void this.refreshDevices().catch(() => undefined);
    })();
    try { await this.audioStart; } finally { this.audioStart = null; }
  }

  prepare(iceServers: IceServer[], direction: 'incoming'|'outgoing'): void {
    this.checkOpen(); if (this.peer) return;
    const peer = this.createPeer({ iceServers: iceServers.map(s => ({ ...s, urls: [...s.urls] })), bundlePolicy: 'max-bundle' });
    this.peer = peer;
    if (direction === 'outgoing') {
      this.audioSender = peer.addTransceiver(this.audioTrack ?? 'audio', { direction: 'sendrecv', streams: this.audioStream ? [this.audioStream] : [] }).sender;
      const local = this.view.get().localVideo;
      this.videoSender = peer.addTransceiver(local?.getVideoTracks()[0] ?? 'video', { direction: 'sendrecv', streams: local ? [local] : [] }).sender;
    }
    peer.onicecandidate = event => {
      if (this.closed || !event.candidate?.candidate) return;
      this.onCandidate?.({ candidate: event.candidate.candidate, sdp_mid: event.candidate.sdpMid, ...(event.candidate.sdpMLineIndex !== null ? { sdp_mline_index: event.candidate.sdpMLineIndex } : {}) });
    };
    peer.onconnectionstatechange = () => {
      if (this.closed) return;
      const state = peer.connectionState;
      if (state === 'connected' || state === 'disconnected' || state === 'failed') this.onConnection?.(state);
      else if (state === 'new' || state === 'connecting') this.onConnection?.('connecting');
    };
    peer.ontrack = event => {
      if (this.closed) return;
      const key = event.track.kind === 'audio' ? 'remoteAudio' : 'remoteVideo';
      const stream = this.view.get()[key] ?? this.stream();
      if (!stream.getTracks().includes(event.track)) stream.addTrack(event.track);
      this.patch({ [key]: stream });
      if (key === 'remoteAudio') { this.audio.srcObject = stream; void this.playAudio(); }
    };
  }
  private connection(): RTCPeerConnection { this.checkOpen(); if (!this.peer) throw new Error('media_not_prepared'); return this.peer; }
  async offer(): Promise<string> {
    const peer = this.connection(), description = await peer.createOffer();
    this.checkOpen(); await peer.setLocalDescription(description); this.checkOpen();
    if (!description.sdp) throw new Error('missing_description'); return description.sdp;
  }
  async answer(sdp: string): Promise<string> {
    const peer = this.connection(); await peer.setRemoteDescription({type:'offer',sdp}); this.checkOpen();
    // Receiving the offer creates its associated transceivers. Pre-created ones would stay unnegotiated.
    const offered = peer.getTransceivers().filter(t => t.mid !== null && t.direction !== 'stopped');
    const audio = offered.find(t => t.receiver.track.kind === 'audio');
    if (!audio) throw new Error('missing_audio_transceiver');
    audio.direction = 'sendrecv'; this.audioSender = audio.sender;
    this.audioSender.setStreams(...(this.audioStream ? [this.audioStream] : []));
    await this.audioSender.replaceTrack(this.audioTrack); this.checkOpen();
    const video = offered.find(t => t.receiver.track.kind === 'video');
    if (video) {
      video.direction = 'sendrecv'; this.videoSender = video.sender;
      const local = this.view.get().localVideo;
      this.videoSender.setStreams(...(local ? [local] : []));
      await this.videoSender.replaceTrack(local?.getVideoTracks()[0] ?? null); this.checkOpen();
    }
    const description = await peer.createAnswer(); this.checkOpen(); await peer.setLocalDescription(description); this.checkOpen();
    if (!description.sdp) throw new Error('missing_description'); return description.sdp;
  }
  async applyAnswer(sdp: string): Promise<void> { await this.connection().setRemoteDescription({type:'answer',sdp}); this.checkOpen(); }
  async addCandidate(payload: SignalPayload): Promise<void> {
    await this.connection().addIceCandidate({ candidate: payload.candidate, sdpMid: payload.sdp_mid ?? null, sdpMLineIndex: payload.sdp_mline_index ?? 0 });
  }
  setMuted(muted: boolean): void { this.muted = muted; if (this.audioTrack) this.audioTrack.enabled = !muted; }

  async setVideoEnabled(enabled: boolean): Promise<void> {
    this.checkOpen(); if (!enabled) { this.stopVideo(); return; }
    if (this.view.get().localVideo) return;
    const epoch = ++this.videoEpoch, selected = this.view.get().selectedCamera;
    if (!this.devices?.getUserMedia) throw new Error('camera_unavailable');
    const stream = await this.devices.getUserMedia({ audio: false, video: { width: {ideal:1280}, height: {ideal:720}, frameRate: {ideal:30,max:30}, ...(selected ? {deviceId:{exact:selected}} : {facingMode:'user'}) } });
    const discard = () => { stream.getTracks().forEach(t => t.stop()); throw new DOMException('camera_cancelled','AbortError'); };
    if (this.closed || epoch !== this.videoEpoch) return discard();
    const track = stream.getVideoTracks()[0];
    if (!track) { stream.getTracks().forEach(t => t.stop()); throw new Error('camera_unavailable'); }
    try { if (this.videoSender) await this.videoSender.replaceTrack(track); }
    catch { stream.getTracks().forEach(t => t.stop()); throw new Error('camera_unavailable'); }
    if (this.closed || epoch !== this.videoEpoch) return discard();
    const settings = track.getSettings();
    track.addEventListener('ended', () => { if (!this.closed && epoch === this.videoEpoch) { this.stopVideo(); this.onVideoFailure?.(); } }, {once:true});
    this.patch({ localVideo: stream, selectedCamera: settings.deviceId ?? selected, mirrored: settings.facingMode !== 'environment' });
    void this.refreshDevices().catch(() => undefined);
  }
  stopVideo(): void {
    this.videoEpoch++;
    this.view.get().localVideo?.getTracks().forEach(t => { t.enabled = false; t.stop(); });
    if (this.videoSender) void this.videoSender.replaceTrack(null).catch(() => undefined);
    this.patch({localVideo:null});
  }
  async selectCamera(id: string): Promise<void> {
    this.checkOpen(); const enabled = this.view.get().localVideo !== null;
    this.patch({selectedCamera:id});
    if (enabled) { this.stopVideo(); await this.setVideoEnabled(true); }
  }
  async switchCamera(): Promise<void> {
    await this.refreshDevices(); const {cameras,selectedCamera} = this.view.get();
    if (cameras.length < 2) return;
    const index = cameras.findIndex(c => c.id === selectedCamera);
    const next = cameras[(index + 1) % cameras.length]; if (next) await this.selectCamera(next.id);
  }
  async refreshDevices(): Promise<void> {
    if (!this.devices?.enumerateDevices || this.closed) return;
    const devices = await this.devices.enumerateDevices(); if (this.closed) return;
    const list = (kind: MediaDeviceKind) => devices.filter(d => d.kind === kind && d.deviceId).map(d => ({id:d.deviceId,label:d.label}));
    this.patch({ cameras: list('videoinput'), outputs: list('audiooutput') });
    const chosen = this.view.get().selectedOutput;
    if (chosen && !devices.some(d => d.kind === 'audiooutput' && d.deviceId === chosen)) await this.selectOutput('').catch(() => undefined);
  }
  async selectOutput(id?: string): Promise<void> {
    this.checkOpen(); if (typeof this.audio.setSinkId !== 'function') throw new Error('output_unavailable');
    const devices = this.devices as OutputDevices | undefined;
    let next = id;
    if (next === undefined && devices?.selectAudioOutput) next = (await devices.selectAudioOutput()).deviceId;
    this.checkOpen(); await this.audio.setSinkId(next ?? ''); this.checkOpen();
    this.patch({ selectedOutput: next ?? '' });
  }
  async playAudio(): Promise<void> {
    if (this.closed || !this.audio.srcObject) return;
    try { await this.audio.play(); if (!this.closed) this.patch({playbackBlocked:false}); }
    catch { if (!this.closed) this.patch({playbackBlocked:true}); }
  }
  async stats(): Promise<MediaSample | null> {
    const peer = this.peer; if (!peer || this.closed) return null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      const report = await Promise.race([peer.getStats(), new Promise<null>(resolve => { timer = setTimeout(() => resolve(null), 500); })]);
      if (!report || this.closed) return null;
      let received = 0, lost = 0, measured = false, rtt: number | null = null;
      const selected = new Set<string>();
      report.forEach(stat => { if (stat.type === 'transport' && typeof stat.selectedCandidatePairId === 'string') selected.add(stat.selectedCandidatePairId); });
      report.forEach(stat => {
        if (stat.type === 'inbound-rtp' && (stat.kind === 'audio' || stat.mediaType === 'audio') && Number.isFinite(stat.packetsReceived) && Number.isFinite(stat.packetsLost)) { received += Number(stat.packetsReceived); lost += Number(stat.packetsLost); measured = true; }
        if (stat.type === 'candidate-pair' && (selected.has(stat.id) || stat.nominated && stat.state === 'succeeded') && Number.isFinite(stat.currentRoundTripTime)) rtt = Number(stat.currentRoundTripTime);
      });
      return measured ? { received, lost, rtt } : null;
    } catch { return null; } finally { clearTimeout(timer); }
  }
  close(): void {
    if (this.closed) return;
    this.closed = true; this.stopVideo();
    this.devices?.removeEventListener('devicechange', this.devicesChanged);
    this.audioStream?.getTracks().forEach(t => t.stop());
    this.view.get().remoteAudio?.getTracks().forEach(t => t.stop());
    this.view.get().remoteVideo?.getTracks().forEach(t => t.stop());
    if (this.peer) { this.peer.ontrack = null; this.peer.onconnectionstatechange = null; this.peer.onicecandidate = null; this.peer.close(); }
    if (this.audio.srcObject) this.audio.pause(); this.audio.srcObject = null;
    this.peer = null; this.audioTrack = null; this.audioStream = null; this.videoSender = null; this.audioSender = null;
    this.onCandidate = undefined; this.onConnection = undefined; this.onVideoFailure = undefined;
    this.patch({ localVideo:null, remoteVideo:null, remoteAudio:null, cameras:[], outputs:[], selectedCamera:'', selectedOutput:'', playbackBlocked:false });
  }
}
